import React, { useContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import * as dt from '../../../shared/src/data.types';
import { authService } from '../services/AuthService';
import {
  publishThemeModeChange,
  readStoredThemeMode,
  writeStoredThemeMode,
} from '../theme/themeStorage';
import { AppContext } from './appContextRef';

 

// Types
export interface User {
  id: dt.IntegerId;
  name: string;
  email: dt.Email;
  role: dt.ALL_USER_ROLES;
  avatar?: string;
  permissions: string[];
  preferences: {
    theme: 'light' | 'dark';
    language: string;
    notifications: boolean;
    autoRefresh: boolean;
  };
  // Profile fields
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  timestamp: Date;
  read: boolean;
  actionable?: boolean;
  actions?: {
    label: string;
    action: () => void;
  }[];
}

export interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: NavigationItem[];
  roles: ('Admin' | 'Lead' | 'Engineer' | 'Support' | 'Deployment')[];
  badge?: number;
  requiredRoute?: string; // Server-computed route capability key from GET /tenant/features/capabilities
  disabledTooltip?: string; // Tooltip to show when route capability is disabled
}

export interface SystemConfig {
  apiBaseUrl: string;
  refreshInterval: number;
  maxRetries: number;
  timeout: number;
  features: {
    fingerprinting: boolean;
    ocr: boolean;
    locationTracking: boolean;
    efemIntegration: boolean;
  };
}

export interface AppState {
  // Authentication & User
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // UI State
  theme: 'light' | 'dark';
  drawerOpen: boolean;
  notifications: Notification[];
  snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  };
  
  // Application State
  systemConfig: SystemConfig;
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
  lastSync: Date | null;
  
  // Cache
  cache: {
    [key: string]: {
      data: unknown;
      timestamp: Date;
      expires: Date;
    };
  };
}

// Action Types
export type AppAction =
  | { type: 'SET_USER'; payload: User }
  | { type: 'UPDATE_USER_PROFILE'; payload: Partial<User> }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_THEME'; payload: 'light' | 'dark' }
  | { type: 'TOGGLE_DRAWER' }
  | { type: 'SET_DRAWER_OPEN'; payload: boolean }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'MARK_NOTIFICATION_READ'; payload: string }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_NOTIFICATIONS' }
  | { type: 'SHOW_SNACKBAR'; payload: { message: string; severity: 'success' | 'error' | 'warning' | 'info' } }
  | { type: 'HIDE_SNACKBAR' }
  | { type: 'UPDATE_CONFIG'; payload: Partial<SystemConfig> }
  | { type: 'SET_CONNECTION_STATUS'; payload: 'connected' | 'disconnected' | 'reconnecting' }
  | { type: 'UPDATE_LAST_SYNC'; payload: Date }
  | { type: 'CACHE_DATA'; payload: { key: string; data: unknown; ttl?: number } }
  | { type: 'CLEAR_CACHE' }
  | { type: 'REMOVE_CACHE_ITEM'; payload: string };

// Get initial theme from localStorage before creating state
const getInitialTheme = (): 'light' | 'dark' => {
  const theme = readStoredThemeMode();
  document.body.setAttribute('data-theme', theme);
  return theme;
};

// Initial State
const initialState: AppState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true to prevent flash of login page
  theme: getInitialTheme(), // Initialize from localStorage immediately
  drawerOpen: true,
  notifications: [],
  snackbar: {
    open: false,
    message: '',
    severity: 'info',
  },
  systemConfig: {
    apiBaseUrl: 'http://localhost:3000/api',
    refreshInterval: 30000,
    maxRetries: 3,
    timeout: 10000,
    features: {
      fingerprinting: true,
      ocr: true,
      locationTracking: true,
      efemIntegration: true,
    },
  },
  connectionStatus: 'connected',
  lastSync: null,
  cache: {},
};

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_USER': {
      const theme = readStoredThemeMode();
      return {
        ...state,
        user: {
          ...action.payload,
          preferences: {
            ...action.payload.preferences,
            theme,
          },
        },
        isAuthenticated: true,
        isLoading: false,
        theme,
      };
    }

    case 'UPDATE_USER_PROFILE':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        cache: {},
        notifications: [],
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    
    case 'SET_THEME':
      return {
        ...state,
        theme: action.payload,
        user: state.user ? {
          ...state.user,
          preferences: {
            ...state.user.preferences,
            theme: action.payload,
          },
        } : null,
      };
    
    case 'TOGGLE_DRAWER':
      return {
        ...state,
        drawerOpen: !state.drawerOpen,
      };
    
    case 'SET_DRAWER_OPEN':
      return {
        ...state,
        drawerOpen: action.payload,
      };
    
    case 'ADD_NOTIFICATION': {
      if (state.notifications.some((n) => n.id === action.payload.id)) {
        return state;
      }
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
      };
    }
    
    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload
            ? { ...notification, read: true }
            : notification
        ),
      };
    
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(notification =>
          notification.id !== action.payload
        ),
      };
    
    case 'CLEAR_NOTIFICATIONS':
      return {
        ...state,
        notifications: [],
      };
    
    case 'SHOW_SNACKBAR':
      return {
        ...state,
        snackbar: {
          open: true,
          message: action.payload.message,
          severity: action.payload.severity,
        },
      };
    
    case 'HIDE_SNACKBAR':
      return {
        ...state,
        snackbar: {
          ...state.snackbar,
          open: false,
        },
      };
    
    case 'UPDATE_CONFIG':
      return {
        ...state,
        systemConfig: {
          ...state.systemConfig,
          ...action.payload,
        },
      };
    
    case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        connectionStatus: action.payload,
      };
    
    case 'UPDATE_LAST_SYNC':
      return {
        ...state,
        lastSync: action.payload,
      };
    
    case 'CACHE_DATA': {
      const { key, data, ttl = 300000 } = action.payload; // Default 5 minutes TTL
      const now = new Date();
      return {
        ...state,
        cache: {
          ...state.cache,
          [key]: {
            data,
            timestamp: now,
            expires: new Date(now.getTime() + ttl),
          },
        },
      };
    }
    
    case 'CLEAR_CACHE':
      return {
        ...state,
        cache: {},
      };
    
    case 'REMOVE_CACHE_ITEM': {
       
      const { [action.payload]: _removed, ...rest } = state.cache;
      return {
        ...state,
        cache: rest,
      };
    }
    
    default:
      return state;
  }
};

type AppContextValue = {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  actions: {
    login: (user: User) => void;
    updateUserProfile: (updates: Partial<User>) => void;
    logout: () => void;
    setTheme: (theme: 'light' | 'dark') => void;
    toggleDrawer: () => void;
    addNotification: (notification: Omit<Notification, 'id' | 'timestamp'> & { id?: string }) => void;
    showSnackbar: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
    cacheData: (key: string, data: unknown, ttl?: number) => void;
    getCachedData: (key: string) => unknown | null;
    clearExpiredCache: () => void;
  };
};

// Provider Component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Initialize user from localStorage and validate
  useEffect(() => {
    const validateAndLoadUser = async () => {
      const savedUser = localStorage.getItem('wafer_inventory_user');
      const accessToken = localStorage.getItem('wafer_access_token');
      const refreshToken = localStorage.getItem('wafer_refresh_token');

      // If we have a saved user AND tokens, validate with API
      if (savedUser && (accessToken || refreshToken)) {
        try {
          const user = JSON.parse(savedUser);

          // Optimistically set user first to avoid flash
          dispatch({ type: 'SET_USER', payload: user });

          // Then validate in background - if it fails, logout will be called
          try {
            const me = await authService.getCurrentUser();
            if (me.user) {
              dispatch({
                type: 'UPDATE_USER_PROFILE',
                payload: {
                  firstName: me.user.firstName,
                  lastName: me.user.lastName,
                  phone: me.user.phone || undefined,
                  avatar: me.user.avatarUrl,
                  name: [me.user.firstName, me.user.lastName].filter(Boolean).join(' ') || user.name,
                },
              });
            }
          } catch (apiError) {
            // API call failed - user is not valid
            // This is normal if backend is down, tokens expired, or network issues
            console.info('User session validation failed during app load - this is normal if you just restarted the backend or your session expired:', apiError instanceof Error ? apiError.message : apiError);
            // Clear everything and show login (this won't cause a redirect during initial load)
            localStorage.removeItem('wafer_inventory_user');
            localStorage.removeItem('wafer_access_token');
            localStorage.removeItem('wafer_refresh_token');
            dispatch({ type: 'LOGOUT' });
          }
        } catch (parseError) {
          console.error('Failed to parse saved user:', parseError);
          localStorage.removeItem('wafer_inventory_user');
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        // No saved user or no tokens, just stop loading
        if (savedUser && !accessToken && !refreshToken) {
          // Clean up orphaned user data
          localStorage.removeItem('wafer_inventory_user');
        }
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    validateAndLoadUser();
  }, []);

  // Persist theme changes to localStorage and notify AppThemeProvider
  useEffect(() => {
    writeStoredThemeMode(state.theme);
    document.body.setAttribute('data-theme', state.theme);
    publishThemeModeChange(state.theme);
  }, [state.theme]);

  // Persist user changes
  useEffect(() => {
    if (state.user) {
      localStorage.setItem('wafer_inventory_user', JSON.stringify(state.user));
    } else {
      localStorage.removeItem('wafer_inventory_user');
    }
  }, [state.user]);

  // Clean up expired cache items
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const expiredKeys = Object.keys(state.cache).filter(key =>
        state.cache[key].expires <= now
      );
      
      expiredKeys.forEach(key => {
        dispatch({ type: 'REMOVE_CACHE_ITEM', payload: key });
      });
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [state.cache]);

  // Monitor connection status
  useEffect(() => {
    const handleOnline = () => dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'connected' });
    const handleOffline = () => dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'disconnected' });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const actions = {
    login: (user: User) => {
      dispatch({ type: 'SET_USER', payload: user });
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: { message: 'Login successful', severity: 'success' }
      });
    },

    updateUserProfile: (updates: Partial<User>) => {
      dispatch({ type: 'UPDATE_USER_PROFILE', payload: updates });
    },

    logout: () => {
      dispatch({ type: 'LOGOUT' });
      dispatch({ 
        type: 'SHOW_SNACKBAR', 
        payload: { message: 'Logged out successfully', severity: 'info' }
      });
    },

    setTheme: (theme: 'light' | 'dark') => {
      dispatch({ type: 'SET_THEME', payload: theme });
    },

    toggleDrawer: () => {
      dispatch({ type: 'TOGGLE_DRAWER' });
    },

    addNotification: (notification: Omit<Notification, 'id' | 'timestamp'> & { id?: string }) => {
      const { id: optionalId, ...rest } = notification;
      const fullNotification: Notification = {
        ...rest,
        id: optionalId ?? `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
      };
      dispatch({ type: 'ADD_NOTIFICATION', payload: fullNotification });
    },

    showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
      dispatch({ type: 'SHOW_SNACKBAR', payload: { message, severity } });
    },

    cacheData: (key: string, data: unknown, ttl?: number) => {
      dispatch({ type: 'CACHE_DATA', payload: { key, data, ttl } });
    },

    getCachedData: (key: string) => {
      const cached = state.cache[key];
      if (!cached) return null;
      
      const now = new Date();
      if (cached.expires <= now) {
        dispatch({ type: 'REMOVE_CACHE_ITEM', payload: key });
        return null;
      }
      
      return cached.data;
    },

    clearExpiredCache: () => {
      const now = new Date();
      Object.keys(state.cache).forEach(key => {
        if (state.cache[key].expires <= now) {
          dispatch({ type: 'REMOVE_CACHE_ITEM', payload: key });
        }
      });
    },
  };

  const contextValue: AppContextValue = { state, dispatch, actions };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use the context
export const useApp = (): AppContextValue => {
  const context = useContext(AppContext) as AppContextValue | null;
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// Additional hooks for specific functionality
export const useAuth = () => {
  const { state, actions } = useApp();
  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    login: actions.login,
    logout: actions.logout,
  };
};

export const useNotifications = () => {
  const { state, dispatch, actions } = useApp();
  return {
    notifications: state.notifications,
    addNotification: actions.addNotification,
    markAsRead: (id: string) => dispatch({ type: 'MARK_NOTIFICATION_READ', payload: id }),
    remove: (id: string) => dispatch({ type: 'REMOVE_NOTIFICATION', payload: id }),
    clear: () => dispatch({ type: 'CLEAR_NOTIFICATIONS' }),
    unreadCount: state.notifications.filter(n => !n.read).length,
  };
};

export const useTheme = () => {
  const { state, actions } = useApp();
  return {
    theme: state.theme,
    setTheme: actions.setTheme,
    toggleTheme: () => actions.setTheme(state.theme === 'light' ? 'dark' : 'light'),
  };
};

export const useCache = () => {
  const { actions } = useApp();
  return {
    cache: actions.cacheData,
    get: actions.getCachedData,
    clearExpired: actions.clearExpiredCache,
  };
};

export default AppContext;